import { useMemo, useState } from "react";
import { api, type SuppressionEntry, type SuppressionCheckResult } from "../api.js";
import { useAsync } from "../useAsync.js";
import { SkeletonTable } from "../Skeleton.js";
import { RefreshButton } from "../RefreshButton.js";

export function Suppression({ org }: { org: string }) {
  const [revision, setRevision] = useState(0);
  const [query, setQuery] = useState("");
  const [checkEmail, setCheckEmail] = useState("");
  const [check, setCheck] = useState<SuppressionCheckResult | null>(null);
  const [message, setMessage] = useState("");
  const [busyEmail, setBusyEmail] = useState("");
  const [importing, setImporting] = useState(false);
  /**
   * Which partition the table shows (#344). The org list alone left out every
   * bounce, complaint and SES-imported address — the entries an operator most
   * needs to see — so "the import did nothing" was a reasonable reading of it.
   */
  const [scope, setScope] = useState<"org" | "global">("org");
  /** Cursor stack for the global list — one entry per page visited, as on Subscribers. */
  const [globalPages, setGlobalPages] = useState<(string | undefined)[]>([undefined]);
  const globalCursor = globalPages[globalPages.length - 1];
  const { data, error, loading, refreshing, refetch } = useAsync(() => api.suppressions(org), [org, revision]);
  // Not read until asked for: it is a second partition, and most visits only
  // need the org list.
  const global = useAsync(
    () => (scope === "global" ? api.globalSuppressions(org, globalCursor) : Promise.resolve(null)),
    [org, scope, globalCursor, revision],
  );

  const rows = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const entries: SuppressionEntry[] = data ?? [];
    return entries
      .filter((entry) => !needle || entry.email.toLowerCase().includes(needle) || entry.source.toLowerCase().includes(needle))
      .sort((a, b) => b.addedAt.localeCompare(a.addedAt));
  }, [data, query]);

  const checkAddress = async () => {
    const email = checkEmail.trim();
    if (!email) return;
    setMessage("");
    try { setCheck(await api.suppressionCheck(org, email)); }
    catch (e) { setMessage(String(e)); setCheck(null); }
  };

  const lift = async (email: string) => {
    setBusyEmail(email); setMessage("");
    try {
      const result = await api.unsuppress(org, email);
      // Only the org entry is ever lifted. Saying "lifted" while a bounce or
      // complaint still blocks the address is how an operator ends up telling a
      // subscriber it is fixed when every send is still refused (#345).
      setMessage(result.stillSuppressedGlobally
        ? `Org suppression lifted for ${email}, but the address is still suppressed account-wide by a bounce or complaint. No mail will reach it.`
        : `Suppression lifted for ${email}.`);
      setRevision((n) => n + 1);
      // Re-checked rather than cleared, so whatever still blocks the address
      // stays on screen.
      if (check?.email === email) setCheck(await api.suppressionCheck(org, email));
    } catch (e) { setMessage(String(e)); }
    finally { setBusyEmail(""); }
  };

  const importSes = async (dryRun: boolean) => {
    setImporting(true); setMessage("");
    try {
      const report = await api.importSuppression(org, dryRun);
      setMessage(`${dryRun ? "Dry run" : "Import"} complete: ${report.read} read, ${report.written} ${dryRun ? "would be written" : "written"}.`);
      if (!dryRun) setRevision((n) => n + 1);
    } catch (e) { setMessage(String(e)); }
    finally { setImporting(false); }
  };

  return (
    <div>
      <div className="pagehead">
        <div>
          <h1>Suppression</h1>
          <p>Addresses that must not receive mail, with local and SES status kept visible. The SES account list is copied in automatically every night at 02:00 UTC; Import SES list does it now.</p>
        </div>
        <div className="row">
          <RefreshButton
            refreshing={refreshing || global.refreshing}
            disabled={loading}
            onClick={() => { void refetch(); if (scope === "global") void global.refetch(); }}
          />
          <button className="btn ghost" disabled={importing} onClick={() => void importSes(true)}>Preview SES import</button>
          <button className="btn" disabled={importing} onClick={() => void importSes(false)}>Import SES list</button>
        </div>
      </div>

      <div className="card">
        <div className="cardhead" style={{ margin: "-18px -18px 16px" }}><h2>Check an address</h2></div>
        <div className="row">
          <input aria-label="Suppression email" value={checkEmail} onChange={(e) => setCheckEmail(e.target.value)} placeholder="email@example.com" onKeyDown={(e) => { if (e.key === "Enter") void checkAddress(); }} style={{ flex: 1, minWidth: 240 }} />
          <button className="btn" disabled={!checkEmail.trim()} onClick={() => void checkAddress()}>Check status</button>
        </div>
        {check && <CheckResult result={check} onLift={lift} busyEmail={busyEmail} />}
      </div>

      {message && <p className="muted">{message}</p>}
      <div className="row" role="group" aria-label="Suppression scope" style={{ marginBottom: 12 }}>
        <button className={`btn ${scope === "org" ? "" : "ghost"}`} aria-pressed={scope === "org"} onClick={() => setScope("org")}>This org</button>
        <button className={`btn ${scope === "global" ? "" : "ghost"}`} aria-pressed={scope === "global"} onClick={() => setScope("global")}>Account-wide</button>
      </div>
      {scope === "org" && <>
        {error && <p className="err">{error}</p>}
        <div className="card">
          <div className="cardhead" style={{ margin: "-18px -18px 16px" }}>
            <h2>Suppressed addresses</h2>
            <input aria-label="Filter suppressions" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Filter email or source" />
          </div>
          {loading && <SkeletonTable rows={5} label="Loading suppression records…" />}
          {!loading && rows.length === 0 && <p className="muted">{query ? "No suppression records match this filter." : "No local suppression records yet."}</p>}
          {rows.length > 0 && <table><thead><tr><th>Email</th><th>Source</th><th>Scope</th><th>Added</th><th /></tr></thead><tbody>
            {rows.map((entry) => <tr key={`${entry.email}:${entry.addedAt}`}><td>{entry.email}</td><td>{entry.source}</td><td>{entry.scope}</td><td>{new Date(entry.addedAt).toLocaleString()}</td><td><button className="btn ghost" disabled={busyEmail === entry.email} onClick={() => void lift(entry.email)}>Lift</button></td></tr>)}
          </tbody></table>}
        </div>
      </>}
      {scope === "global" && <>
        {global.error && <p className="err">{global.error}</p>}
        <div className="card">
          <div className="cardhead" style={{ margin: "-18px -18px 16px" }}><h2>Account-wide suppressions</h2></div>
          {/* Read-only, and said so: nothing in the console or API removes a
              global entry, and these block the address for every newsroom on
              the deployment, not just this one. No filter box either — it could
              only search the page on screen, which reads as "not suppressed". */}
          <p className="muted" style={{ marginTop: 0 }}>
            Hard bounces, complaints and the SES account import. These block an address for <strong>every newsroom</strong>, and cannot be lifted from this console. Use “Check an address” to look one up.
          </p>
          {global.loading && <SkeletonTable rows={5} label="Loading account-wide suppressions…" />}
          {global.data && global.data.rows.length === 0 && <p className="muted">No account-wide suppressions.</p>}
          {global.data && global.data.rows.length > 0 && <table><thead><tr><th>Email</th><th>Source</th><th>Scope</th><th>Added</th></tr></thead><tbody>
            {global.data.rows.map((entry) => <tr key={entry.email}><td>{entry.email}</td><td>{entry.source}</td><td>account-wide</td><td>{new Date(entry.addedAt).toLocaleString()}</td></tr>)}
          </tbody></table>}
          {global.data && (globalPages.length > 1 || global.data.cursor) && (
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
              <button className="btn ghost" disabled={globalPages.length === 1} onClick={() => setGlobalPages(globalPages.slice(0, -1))}>Previous</button>
              <button className="btn ghost" disabled={!global.data.cursor} onClick={() => setGlobalPages([...globalPages, global.data!.cursor])}>Next</button>
              <span className="muted">Page {globalPages.length}</span>
            </div>
          )}
        </div>
      </>}
    </div>
  );
}

function CheckResult({ result, onLift, busyEmail }: { result: SuppressionCheckResult; onLift: (email: string) => Promise<void>; busyEmail: string }) {
  const hasLocal = result.local.length > 0;
  // Lift only ever removes the org entry (#345). Offering it for a global-only
  // address promised a fix it could not deliver.
  const hasOrg = result.local.some((entry) => entry.scope === "org");
  const globalEntries = result.local.filter((entry) => entry.scope === "global");
  return <div className="card" style={{ margin: "16px 0 0", background: "var(--surface-2)" }}>
    <div className="row" style={{ justifyContent: "space-between" }}><strong>{result.email}</strong><span className={`pill ${hasLocal ? "p-warn" : "p-good"}`}>{hasLocal ? "Suppressed" : "Not locally suppressed"}</span></div>
    {hasLocal && <p className="muted">{result.local.map((entry) => `${entry.source} · ${entry.scope === "global" ? "account-wide" : entry.scope}`).join("; ")}</p>}
    {globalEntries.length > 0 && <p className="err">
      Globally suppressed: a {globalEntries.map((entry) => entry.source).join(" and ")} blocks this address for every newsroom. This screen cannot lift it{hasOrg ? ", so lifting the org suppression will not make the address mailable" : ""}.
    </p>}
    {result.live && <p className="muted">SES: {result.live.reason}{result.live.at ? ` · ${new Date(result.live.at).toLocaleString()}` : ""}</p>}
    {result.live === null && <p className="muted">SES: clear</p>}
    {result.liveError && <p className="muted">SES check unavailable: {result.liveError}</p>}
    {hasOrg && <button className="btn ghost" disabled={busyEmail === result.email} onClick={() => void onLift(result.email)}>Lift org suppression</button>}
  </div>;
}
